/**
 * Hilfsfunktionen für die OCR-UI im Fahrzeug-Detail-Drawer (Issue #794)
 */

export const OCR_KATEGORIEN = [
  'zulassung_zb1',
  'zulassung_zb2',
  'leasingvertrag',
  'versicherungspolice',
] as const;

export type OcrKategorie = typeof OCR_KATEGORIEN[number];
export type OcrStatus = 'ausstehend' | 'abgeschlossen' | 'fehler' | 'keine_felder';

export interface OcrRun {
  fehler: string | null;
  created_at: string;
}

// ─── Kategorienfilter ─────────────────────────────────────────────────────────

export function isOcrRelevantKategorie(kategorie: string): boolean {
  return (OCR_KATEGORIEN as readonly string[]).includes(kategorie);
}

// ─── Status-Berechnung ────────────────────────────────────────────────────────

export function getOcrStatus(
  dok: { ocr_extracted_fields?: unknown | null },
  ocrRuns: OcrRun[],
): OcrStatus {
  if (dok.ocr_extracted_fields !== null && dok.ocr_extracted_fields !== undefined) {
    const fields = dok.ocr_extracted_fields;
    if (typeof fields === 'object' && Object.keys(fields as object).length === 0) {
      return 'keine_felder';
    }
    return 'abgeschlossen';
  }
  const hasError = ocrRuns.some(r => r.fehler !== null && r.fehler !== undefined);
  return hasError ? 'fehler' : 'ausstehend';
}

// ─── Feld-Labels (Deutsch) ────────────────────────────────────────────────────

const FIELD_LABELS: Record<string, string> = {
  fin:                  'FIN',
  hsn:                  'HSN',
  tsn:                  'TSN',
  erstzulassung:        'Erstzulassung',
  marke:                'Marke / Hersteller',
  kw:                   'Leistung (kW)',
  ccm:                  'Hubraum (ccm)',
  vertragsnr:           'Vertragsnummer',
  rate:                 'Monatliche Rate',
  laufzeit:             'Laufzeit (Monate)',
  blp:                  'Brutto-Listpreis',
  sonderzahlung:        'Sonderzahlung',
  versicherer:          'Versicherer',
  police_nr:            'Policen-Nr.',
  jahrespraemie:        'Jahresprämie',
  deckungssumme:        'Deckungssumme',
  vertragsbeginn:       'Vertragsbeginn',
};

export function getFieldLabel(feldname: string): string {
  return FIELD_LABELS[feldname] ?? feldname;
}

// ─── Wert & Konfidenz aus ocr_extracted_fields ────────────────────────────────

export function getOcrFieldValue(fields: unknown, feldname: string): string | null {
  if (!fields || typeof fields !== 'object') return null;
  const val = (fields as Record<string, unknown>)[feldname];
  if (val === undefined || val === null) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && 'wert' in (val as object)) {
    const wert = (val as Record<string, unknown>).wert;
    return typeof wert === 'string' ? wert : null;
  }
  return null;
}

export function getOcrFieldKonfidenz(fields: unknown, feldname: string): number | null {
  if (!fields || typeof fields !== 'object') return null;
  const val = (fields as Record<string, unknown>)[feldname];
  if (!val || typeof val !== 'object') return null;
  const k = (val as Record<string, unknown>).konfidenz;
  return typeof k === 'number' ? k : null;
}

// ─── OCR-Feld → fahrzeuge-Spalte Mapping ─────────────────────────────────────

const OCR_TO_FAHRZEUG: Record<string, string> = {
  fin:          'fin',
  erstzulassung:'erstzulassung',
  marke:        'marke',
  versicherer:  'versicherung_gesellschaft',
  police_nr:    'versicherung_police_nr',
  rate:         'monatliche_rate',
  blp:          'bruttolistenpreis',
};

export function getFahrzeugFeldFuerOcr(ocrFeld: string): string | null {
  return OCR_TO_FAHRZEUG[ocrFeld] ?? null;
}

// ─── Numerische Felder ───────────────────────────────────────────────────────

export const NUMERIC_FAHRZEUG_FELDER = new Set(['bruttolistenpreis', 'monatliche_rate']);

export function parseEuropeanNumber(wert: string): number | null {
  const normalized = wert.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(normalized);
  return isNaN(n) ? null : n;
}

// ─── Bereits-übernommen-Erkennung ─────────────────────────────────────────────

export function isFieldAdopted(
  ocrWert: string,
  fahrzeug: Record<string, unknown>,
  fahrzeugFeld: string,
): boolean {
  const current = fahrzeug[fahrzeugFeld];
  if (current === null || current === undefined) return false;
  if (NUMERIC_FAHRZEUG_FELDER.has(fahrzeugFeld)) {
    const parsed = parseEuropeanNumber(ocrWert);
    return parsed !== null && parsed === Number(current);
  }
  return String(current) === ocrWert;
}
